import {
    ChartEditorAccountRef,
    ChartEditorAddAccountBody,
    ChartEditorAddGroupBody,
    ChartEditorGroupRef,
    ChartEditorMoveNeighbour,
    ChartEditorMoveRow,
} from "@common/schemas/domains/accounting/chart-editor.schema";
import { ChartEditorWireOperation } from "@api/chartEditor.api";
import { DraftAccount, DraftGroup, DraftState } from "../draftState";
import { CoaEditorCommand } from "../nested/coaEditorCommand";

// A group's draft id is local to this session, so the wire addresses it by the
// ancestor names it had BEFORE this gesture ran. Every emitter mutates the
// draft and calls `onOperation` second, so a path read afterwards would be the
// renamed, moved or deleted state and would not resolve on the server.
interface GroupPathNode {
    name: string;
    parentGroupId: string | null;
}

interface TranslationIndex {
    accountsById: Map<string, DraftAccount>;
    groupsById: Map<string, DraftGroup>;
    pathNodesById: Map<string, GroupPathNode>;
}

type ParentGroupResolution =
    | { isResolved: true; parentGroup: { namePath: string[] } | null }
    | { isResolved: false };

const buildIndex = (
    draft: DraftState,
    mintedGroups: Array<{
        groupId: string;
        parentGroupId: string | null;
        name: string;
    }>,
): TranslationIndex => {
    const pathNodesById = new Map<string, GroupPathNode>(
        draft.groups.map((group) => [
            group.id,
            { name: group.name, parentGroupId: group.parentGroupId },
        ]),
    );
    // The one thing the pre-gesture draft lacks is a destination folder this
    // same gesture minted; its parent chains back into that draft.
    for (const minted of mintedGroups) {
        pathNodesById.set(minted.groupId, {
            name: minted.name,
            parentGroupId: minted.parentGroupId,
        });
    }
    return {
        accountsById: new Map(
            draft.accounts.map((account) => [account.id, account]),
        ),
        groupsById: new Map(draft.groups.map((group) => [group.id, group])),
        pathNodesById,
    };
};

const resolveNamePath = (
    groupId: string,
    pathNodesById: Map<string, GroupPathNode>,
): string[] | null => {
    const namePath: string[] = [];
    let currentId: string | null = groupId;
    while (currentId !== null) {
        const node = pathNodesById.get(currentId);
        if (node === undefined || namePath.length > pathNodesById.size) {
            return null;
        }
        namePath.unshift(node.name);
        currentId = node.parentGroupId;
    }
    return namePath;
};

const resolveParentGroup = (
    parentGroupId: string | null,
    pathNodesById: Map<string, GroupPathNode>,
): ParentGroupResolution => {
    if (parentGroupId === null) {
        return { isResolved: true, parentGroup: null };
    }
    const namePath = resolveNamePath(parentGroupId, pathNodesById);
    return namePath === null
        ? { isResolved: false }
        : { isResolved: true, parentGroup: { namePath } };
};

const toAccountRefs = (
    accountIds: string[],
    index: TranslationIndex,
): ChartEditorAccountRef[] | null => {
    const refs: ChartEditorAccountRef[] = [];
    for (const accountId of accountIds) {
        const account = index.accountsById.get(accountId);
        if (account === undefined) {
            return null;
        }
        refs.push({ entityId: account.entityId, accountId });
    }
    return refs;
};

const toGroupRefs = (
    groupIds: string[],
    index: TranslationIndex,
): ChartEditorGroupRef[] | null => {
    const refs: ChartEditorGroupRef[] = [];
    for (const groupId of groupIds) {
        const group = index.groupsById.get(groupId);
        const namePath = resolveNamePath(groupId, index.pathNodesById);
        if (group === undefined || namePath === null) {
            return null;
        }
        refs.push({
            entityId: group.entityId,
            type: group.type,
            namePath,
        });
    }
    return refs;
};

const trimToNull = (value: string | null): string | null => {
    const trimmed = value?.trim() ?? "";
    return trimmed.length > 0 ? trimmed : null;
};

const translateUpdateAccount = (
    command: Extract<CoaEditorCommand, { type: "updateAccount" }>,
    index: TranslationIndex,
): ChartEditorWireOperation | null => {
    const accounts = toAccountRefs(command.accountIds, index);
    if (accounts === null || accounts.length === 0) {
        return null;
    }
    const name =
        command.patch.name === undefined
            ? undefined
            : trimToNull(command.patch.name);
    if (name === null) {
        return null;
    }
    return {
        type: "updateAccount",
        body: {
            accounts,
            name,
            codeText:
                command.patch.codeText === undefined
                    ? undefined
                    : trimToNull(command.patch.codeText),
        },
    };
};

const translateAddAccount = (
    command: Extract<CoaEditorCommand, { type: "addAccount" }>,
    index: TranslationIndex,
): ChartEditorWireOperation | null => {
    const name = trimToNull(command.name);
    if (name === null) {
        return null;
    }
    const created: ChartEditorAddAccountBody["created"] = [];
    for (const row of command.created) {
        const parent = resolveParentGroup(
            row.parentGroupId,
            index.pathNodesById,
        );
        if (!parent.isResolved) {
            return null;
        }
        created.push({
            entityId: row.entityId,
            tempId: row.accountId,
            parentGroup: parent.parentGroup,
            position: row.position,
            // A pending row carries whatever code was typed before the name
            // landed; omitting the field would store the allocated number.
            codeText: trimToNull(
                index.accountsById.get(row.accountId)?.codeText ?? null,
            ),
        });
    }
    if (created.length === 0) {
        return null;
    }
    return {
        type: "addAccount",
        body: { accountType: command.accountType, name, created },
    };
};

const translateAddGroup = (
    command: Extract<CoaEditorCommand, { type: "addGroup" }>,
    index: TranslationIndex,
): ChartEditorWireOperation | null => {
    const name = trimToNull(command.name);
    if (name === null || command.created.length === 0) {
        return null;
    }
    const created: ChartEditorAddGroupBody["created"] = [];
    for (const row of command.created) {
        const parent = resolveParentGroup(
            row.parentGroupId,
            index.pathNodesById,
        );
        if (!parent.isResolved) {
            return null;
        }
        created.push({
            entityId: row.entityId,
            parentGroup: parent.parentGroup,
            position: row.position,
        });
    }
    return {
        type: "addGroup",
        body: { accountType: command.accountType, name, created },
    };
};

const translateDelete = (
    command: Extract<CoaEditorCommand, { type: "delete" }>,
    index: TranslationIndex,
): ChartEditorWireOperation | null => {
    const accounts = toAccountRefs(command.accountIds, index);
    const groups = toGroupRefs(command.groupIds, index);
    if (accounts === null || groups === null) {
        return null;
    }
    if (accounts.length === 0 && groups.length === 0) {
        return null;
    }
    return { type: "deleteRows", body: { accounts, groups } };
};

const resolveNeighbour = (
    row: Extract<CoaEditorCommand, { type: "move" }>["rows"][number],
    movedIds: Set<string>,
    index: TranslationIndex,
): ChartEditorMoveNeighbour | null => {
    if (row.overRefId === null || movedIds.has(row.overRefId)) {
        return { kind: "append" };
    }
    if (index.accountsById.has(row.overRefId)) {
        return {
            kind: "account",
            accountId: row.overRefId,
            position: row.position,
        };
    }
    const namePath = resolveNamePath(row.overRefId, index.pathNodesById);
    return namePath === null
        ? null
        : { kind: "group", namePath, position: row.position };
};

const translateMove = (
    command: Extract<CoaEditorCommand, { type: "move" }>,
    index: TranslationIndex,
): ChartEditorWireOperation | null => {
    if (command.rows.length === 0) {
        return null;
    }
    // Dropping onto another row of this same block is not a vanished
    // neighbour: the draft's splice appends there, and so must the wire.
    const movedIds = new Set(command.rows.map((row) => row.constituentId));
    const rows: ChartEditorMoveRow[] = [];
    for (const row of command.rows) {
        const destination = resolveParentGroup(
            row.targetParentGroupId,
            index.pathNodesById,
        );
        if (!destination.isResolved) {
            return null;
        }
        const neighbour = resolveNeighbour(row, movedIds, index);
        if (neighbour === null) {
            return null;
        }
        const base = {
            entityId: row.entityId,
            targetType: row.targetType,
            destinationParentGroup: destination.parentGroup,
            neighbour,
        };
        if (row.kind === "account") {
            rows.push({
                ...base,
                kind: "account",
                accountId: row.constituentId,
            });
            continue;
        }
        const namePath = resolveNamePath(
            row.constituentId,
            index.pathNodesById,
        );
        if (namePath === null) {
            return null;
        }
        // Draft still has the pre-move Type; the server looks the group up by that.
        const sourceType =
            index.groupsById.get(row.constituentId)?.type ?? row.targetType;
        rows.push({ ...base, kind: "group", namePath, sourceType });
    }
    return { type: "move", body: { rows } };
};

// Translates one editor-log command into the body the executor queues, reading
// group paths off the draft as it stood BEFORE this gesture applied. `null`
// means nothing should be sent — an unnamed pending add, an empty gesture, or a
// ref the pre-gesture draft cannot resolve.
export const translateEditorCommand = ({
    command,
    draft,
}: {
    command: CoaEditorCommand;
    draft: DraftState;
}): ChartEditorWireOperation | null => {
    const index = buildIndex(
        draft,
        command.type === "move" ? command.createdGroups : [],
    );
    switch (command.type) {
        case "updateAccount":
            return translateUpdateAccount(command, index);
        case "addAccount":
            return translateAddAccount(command, index);
        case "addGroup":
            return translateAddGroup(command, index);
        case "renameGroup": {
            const groups = toGroupRefs(command.groupIds, index);
            const name = trimToNull(command.name);
            return groups === null || name === null || groups.length === 0
                ? null
                : { type: "renameGroup", body: { name, groups } };
        }
        case "delete":
            return translateDelete(command, index);
        case "setAccountDisabled": {
            const accounts = toAccountRefs(command.accountIds, index);
            return accounts === null || accounts.length === 0
                ? null
                : {
                      type: "setAccountDisabled",
                      body: { isDisabled: command.isDisabled, accounts },
                  };
        }
        case "move":
            return translateMove(command, index);
    }
};
